import {
  ConfigurationType,
  ConfigurationToName,
  SelectedDependencyWithClearHistoryProps,
} from './types';
import { Fragment, useMemo } from 'react';
import { DialogLoader } from '../../common/dialogs/DialogLoader';
import Box from '@mui/material/Box';
import { DefaultConfigurationTable } from '../configuration-item-tables/DefaultConfigurationTable';
import { FieldsTable } from '../configuration-item-tables/FieldsTable';
import { FlowsTable } from '../configuration-item-tables/FlowsTable';
import { ObjectsTable } from '../configuration-item-tables/ObjectsTable';
import { CpqTables } from '../configuration-item-tables/CpqTables';
import groupBy from 'lodash/groupBy';
import { RelatedObjectsTable } from '../configuration-item-tables/RelatedObjectsTable';
import { PossibleSectionInDependencies } from './search/types';
import {
  ConfigurationItem,
  ConfigurationItemField,
  ConfigurationItemRollup,
} from '../../parser/ParserTypes';

export interface DependenciesTableMap {
  [key: string]: ConfigurationItem[];
}

interface DependenciesTableProps {
  configurationItems: DependenciesTableMap;
  onChooseNewRule: (props: SelectedDependencyWithClearHistoryProps) => void;
  listViewCounter?: number;
  isLoadingDependency?: boolean;
  activeItemId?: string;
  crmOrgId: string;
  clearHistory?: boolean;
  section?: PossibleSectionInDependencies;
}

export const DependenciesTable = ({
  configurationItems,
  onChooseNewRule,
  listViewCounter,
  isLoadingDependency,
  activeItemId,
  clearHistory,
  crmOrgId,
  section = 'dependencies',
}: DependenciesTableProps) => {
  const configurationKeys = useMemo(
    () => (configurationItems ? Object.keys(configurationItems) : []),
    [configurationItems],
  );

  const renderedTables = useMemo(() => {
    if (!configurationItems) {
      return [];
    }
    return configurationKeys.map((confKey, idx) => {
      const key = confKey as ConfigurationType;
      const items = configurationItems[key];

      if (!items.length) {
        return <Fragment key={`no-configuration-items_${idx}`} />;
      }

      // Handle the 'others' case separately to avoid extra nesting
      if (key === ConfigurationType.others) {
        const groupedItems = groupBy(items, 'type');

        return (
          <DependenciesTable
            key={key}
            configurationItems={groupedItems}
            onChooseNewRule={onChooseNewRule}
            listViewCounter={listViewCounter}
            isLoadingDependency={isLoadingDependency}
            activeItemId={activeItemId}
            clearHistory={clearHistory}
            crmOrgId={crmOrgId}
            section={section}
          />
        );
      }

      const createTableContent = () => {
        switch (key) {
          case ConfigurationType.fields: {
            return (
              <FieldsTable
                key={key}
                onFieldClick={onChooseNewRule}
                items={items as ConfigurationItemField[]}
                activeItemId={activeItemId}
                clearHistory={clearHistory}
                tableHeader="Fields"
                section={section}
              />
            );
          }

          case ConfigurationType.rollups: {
            return (
              <FieldsTable
                key={key}
                onFieldClick={onChooseNewRule}
                items={items as ConfigurationItemRollup[]}
                activeItemId={activeItemId}
                clearHistory={clearHistory}
                tableHeader="Rollups"
                dataTableType="rollups"
                section={section}
              />
            );
          }

          case ConfigurationType.layouts:
            return (
              <DefaultConfigurationTable
                tableHeader={ConfigurationToName[key]}
                items={items}
                onItemClick={onChooseNewRule}
                key={key}
                parentType={key}
                activeItemId={activeItemId}
                clearHistory={clearHistory}
                section={section}
              />
            );

          case ConfigurationType.flows:
            return (
              <FlowsTable
                key={key}
                items={items}
                onFlowClick={onChooseNewRule}
                activeItemId={activeItemId}
                clearHistory={clearHistory}
                section={section}
              />
            );

          case ConfigurationType.relatedObjects:
            return (
              <RelatedObjectsTable
                key={key}
                items={items}
                onItemClick={onChooseNewRule}
                clearHistory={clearHistory}
                section={section}
              />
            );

          case ConfigurationType.objects:
            return (
              <ObjectsTable
                key={key}
                items={items.map((item) => ({
                  id: item.metadataId,
                  name: item.name,
                  label: item.label,
                  metadataId: item.metadataId,
                }))}
                onItemClick={onChooseNewRule}
                clearHistory={clearHistory}
                section={section}
              />
            );

          case ConfigurationType.cpqData:
            return (
              <CpqTables
                crmOrgId={crmOrgId}
                key={key}
                items={items}
                onItemClick={onChooseNewRule}
                clearHistory={!!clearHistory}
                activeItemId={activeItemId}
                section={section}
              />
            );

          default:
            return (
              <DefaultConfigurationTable
                tableHeader={ConfigurationToName[key] ?? key}
                items={items}
                onItemClick={onChooseNewRule}
                key={key}
                parentType={key}
                activeItemId={activeItemId}
                clearHistory={clearHistory}
                section={section}
              />
            );
        }
      };

      const tableContent = createTableContent();

      return <Fragment key={key}>{tableContent}</Fragment>;
    });
  }, [
    configurationKeys,
    configurationItems,
    onChooseNewRule,
    listViewCounter,
    activeItemId,
    clearHistory,
    crmOrgId,
    isLoadingDependency,
    section,
  ]);

  if (!configurationItems) {
    return <></>;
  }

  return (
    <Box
      sx={{
        '& .MuiTableBody-root .MuiTableRow-root': {
          '& .chevron-button': {
            opacity: 0,

            '& > .MuiButton-root': {
              minWidth: 0, //to fix table overflow
            },
          },

          '&:hover .chevron-button': {
            opacity: 1,
          },
        },
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <DialogLoader visible={isLoadingDependency} variant="light" />
      {renderedTables}
    </Box>
  );
};
